// Screen: Bitcoin Clock - Block Timeline
import React, { useEffect, useState } from 'react';

const BitcoinClockWatchTimeline = ({ btcPrice, blockHeight, stats, prevBlockTime, nextBlockTime }) => {
  // Array of two-part motivational texts (moved to top before useState)
  const motivationalTexts = [
    { first: "Tick Tock", second: "Next Block!" },
    { first: "Stay Humble", second: "Stack Sats!" },
    { first: "HODL Strong", second: "Diamond Hands!" },
    { first: "Bitcoin Only", second: "No Coiner!" },
    { first: "Bitcoin", second: "To The Moon!" },
    { first: "Orange Pill", second: "Wake Up!" },
    { first: "Be Patient", second: "Time Will Tell!" },
    { first: "Trust The Code", second: "Not The Hype!" },
    { first: "Long Term", second: "Think Long!" },
    { first: "Satoshi's Vision", second: "Never Forget!" },
    { first: "Accumulate", second: "Don't Sell!" },
    { first: "Peace Money", second: "For The World!" },
    { first: "Digital Gold", second: "Store Value!" },
    { first: "Freedom Tech", second: "Own Your Keys!" },
    { first: "Sound Money", second: "Forever!" },
    { first: "Laser Eyes", second: "On!" },
    { first: "When Lambo?", second: "Soon!" },
    { first: "No Sleep", second: "Just Stack!" },
    { first: "Bitcoin Pizza", second: "Day!" },
    { first: "Moon Mission", second: "Accepted!" },
    { first: "Bitcoin Winter?", second: "Buy More!" },
    { first: "Bear Market?", second: "Accumulate!" },
    { first: "FOMO?", second: "HODL!" },
    { first: "Paper Hands?", second: "Not Today!" },
    { first: "Diamond Hands", second: "Forever!" },
    { first: "Satoshi Lives", second: "In Code!" },
    { first: "Genesis Block", second: "2009!" },
    { first: "Halving Soon", second: "Get Ready!" },
    { first: "Block Reward", second: "Halves!" },
    { first: "Supply Limited", second: "21 Million!" },
    { first: "Deflationary", second: "Asset!" },
    { first: "Censorship", second: "Resistant!" },
    { first: "Decentralized", second: "Money!" },
    { first: "Peer to Peer", second: "Cash!" },
    { first: "Lightning Fast", second: "Payments!" },
    { first: "Borderless", second: "Money!" },
    { first: "Permissionless", second: "Innovation!" },
    { first: "Open Source", second: "Forever!" },
    { first: "Math Based", second: "Trust!" },
    { first: "Scarce Digital", second: "Gold!" }
  ];

  const [halvingData, setHalvingData] = useState(null);
  const [timelineItems, setTimelineItems] = useState([]);
  const [animateGlow, setAnimateGlow] = useState(false);
  const [previousBlockHeight, setPreviousBlockHeight] = useState(null);
  const [currentHalvingIndex, setCurrentHalvingIndex] = useState(0);
  const [currentTextIndex, setCurrentTextIndex] = useState(Math.floor(Math.random() * motivationalTexts.length));
  const [textTransition, setTextTransition] = useState(false);
  const [blockChangeAnimation, setBlockChangeAnimation] = useState(false);
  const currentTime = Date.now() / 1000;

  // Get API URL
  const getApiUrl = () => {
    if (window.location.hostname === 'clock.test.local' ||
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1') {
      return "http://bitcoin.api.test.local/v1/";
    }
    return "https://bitcoin.api.bitcoinagree.com/v1/";
  };

  useEffect(() => {
    // Fetch halving data
    const fetchHalvingData = async () => {
      try {
        const response = await fetch(`${getApiUrl()}halvings`);
        const data = await response.json();
        setHalvingData(data);
      } catch (error) {
        console.error('Error fetching halving data:', error);
      }
    };

    fetchHalvingData();
  }, []);

  useEffect(() => {
    if (!halvingData || !blockHeight) return;

    const items = [];
    const halvings = halvingData.halvings;

    // Find current halving period
    let currentHalvingIndex = 0;
    for (let i = 0; i < halvings.length - 1; i++) {
      if (blockHeight >= halvings[i].block && blockHeight < halvings[i + 1].block) {
        currentHalvingIndex = i;
        break;
      }
    }

    // Previous halving
    if (currentHalvingIndex > 0) {
      const prevHalving = halvings[currentHalvingIndex];
      items.push({
        type: 'prev-halving',
        block: prevHalving.block,
        time: prevHalving.timestamp,
        reward: prevHalving.reward,
        label: 'Previous Halving'
      });
    }

    // Current block
    items.push({
      type: 'current',
      block: blockHeight,
      time: currentTime,
      label: 'Current Block'
    });

    // Next halving
    if (currentHalvingIndex < halvings.length - 1) {
      const nextHalving = halvings[currentHalvingIndex + 1];
      items.push({
        type: 'next-halving',
        block: nextHalving.block,
        time: nextHalving.timestamp,
        reward: nextHalving.reward,
        label: 'Next Halving'
      });
    }

    setTimelineItems(items);
  }, [halvingData, blockHeight, currentTime]);

  // Update current halving index whenever halving data or block height changes
  useEffect(() => {
    if (!halvingData || !blockHeight) return;

    const halvings = halvingData.halvings;
    let index = 0;
    for (let i = 0; i < halvings.length - 1; i++) {
      if (blockHeight >= halvings[i].block && blockHeight < halvings[i + 1].block) {
        index = i;
        break;
      }
    }
    setCurrentHalvingIndex(index);
  }, [halvingData, blockHeight]);

  const getItemColor = (item) => {
    if (item.type === 'current') return "#f7931a"; // Current block - Bitcoin orange
    if (item.type === 'prev-halving') return "#01de01ff"; // Previous halving - Green
    if (item.type === 'next-halving') return "#0095ffff"; // Next halving - Bright blue
    return "#666";
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString([], {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate days remaining until next halving
  const getDaysUntilHalving = (halvingTimestamp) => {
    const now = Date.now() / 1000;
    const timeDiff = halvingTimestamp - now;
    const daysRemaining = Math.ceil(timeDiff / (24 * 60 * 60)); // Convert seconds to days
    return daysRemaining > 0 ? daysRemaining : 0;
  };

  // Calculate days since previous halving occurred
  const getDaysSinceHalving = (halvingTimestamp) => {
    const now = Date.now() / 1000;
    const timeDiff = now - halvingTimestamp;
    const daysSince = Math.floor(timeDiff / (24 * 60 * 60)); // Convert seconds to days
    return daysSince > 0 ? daysSince : 0;
  };

  // Calculate months since previous halving occurred
  const getMonthsSinceHalving = (halvingTimestamp) => {
    const now = Date.now() / 1000;
    const timeDiff = now - halvingTimestamp;
    const monthsSince = Math.floor(timeDiff / (30.44 * 24 * 60 * 60)); // Convert seconds to months (average month)
    return monthsSince > 0 ? monthsSince : 0;
  };

  // Calculate months remaining until next halving
  const getMonthsUntilHalving = (halvingTimestamp) => {
    const now = Date.now() / 1000;
    const timeDiff = halvingTimestamp - now;
    const monthsRemaining = Math.ceil(timeDiff / (30.44 * 24 * 60 * 60)); // Convert seconds to months
    return monthsRemaining > 0 ? monthsRemaining : 0;
  };

  // Calculate percentage of time remaining until next halving
  const getHalvingProgressPercent = () => {
    if (!halvingData || !blockHeight) return 0;

    const halvings = halvingData.halvings;
    let currentHalvingIndex = 0;

    // Find current halving period
    for (let i = 0; i < halvings.length - 1; i++) {
      if (blockHeight >= halvings[i].block && blockHeight < halvings[i + 1].block) {
        currentHalvingIndex = i;
        break;
      }
    }

    if (currentHalvingIndex >= halvings.length - 1) return 0;

    const prevHalving = halvings[currentHalvingIndex];
    const nextHalving = halvings[currentHalvingIndex + 1];
    const now = Date.now() / 1000;

    const totalPeriod = nextHalving.timestamp - prevHalving.timestamp;
    const elapsedPeriod = now - prevHalving.timestamp;

    if (totalPeriod <= 0) return 0;

    const percentRemaining = ((totalPeriod - elapsedPeriod) / totalPeriod) * 100;
    return Math.max(0, Math.min(100, Math.round(percentRemaining)));
  };

  // Calculate percentage of time elapsed since previous halving
  const getPrevHalvingElapsedPercent = () => {
    if (!halvingData || !blockHeight) return 0;

    const halvings = halvingData.halvings;
    let currentHalvingIndex = 0;

    // Find current halving period
    for (let i = 0; i < halvings.length - 1; i++) {
      if (blockHeight >= halvings[i].block && blockHeight < halvings[i + 1].block) {
        currentHalvingIndex = i;
        break;
      }
    }

    if (currentHalvingIndex < 1) return 100; // If we're in the first halving period

    const prevPrevHalving = halvings[currentHalvingIndex - 1];
    const prevHalving = halvings[currentHalvingIndex];
    const now = Date.now() / 1000;

    const totalPeriod = prevHalving.timestamp - prevPrevHalving.timestamp;
    const elapsedPeriod = now - prevHalving.timestamp;

    if (totalPeriod <= 0) return 100;

    const percentElapsed = (elapsedPeriod / totalPeriod) * 100;
    return Math.max(0, Math.min(100, Math.round(percentElapsed)));
  };

  // Timer to trigger Bitcoin logo glow effect every 30 seconds
  useEffect(() => {
    console.log('Setting up glow timer...');
    const glowTimer = setInterval(() => {
      console.log('Glow timer triggered - setting animateGlow to true');
      setAnimateGlow(true);
      setTimeout(() => {
        console.log('Glow timeout - setting animateGlow to false');
        setAnimateGlow(false);
      }, 2000); // Glow for 2 seconds
    }, 30000); // Every 30 seconds

    return () => {
      console.log('Cleaning up glow timer');
      clearInterval(glowTimer);
    };
  }, []);

  // Timer to switch motivational texts every 30 seconds (temporary for testing)
  useEffect(() => {
    console.log('Setting up text timer...');
    const textTimer = setInterval(() => {
      console.log('Text timer triggered - starting transition');
      setTextTransition(true);
      setTimeout(() => {
        const newIndex = Math.floor(Math.random() * motivationalTexts.length);
        console.log('Text timeout - changing to index:', newIndex, 'first:', motivationalTexts[newIndex].first, 'second:', motivationalTexts[newIndex].second);
        setCurrentTextIndex(newIndex);
        setTextTransition(false);
      }, 300); // Half of transition duration
    }, 30000); // Switch every 30 seconds (temporary for testing)

    return () => {
      console.log('Cleaning up text timer');
      clearInterval(textTimer);
    };
  }, []);

  // Detect block height changes and trigger animation
  useEffect(() => {
    if (blockHeight !== null && previousBlockHeight !== null && blockHeight !== previousBlockHeight) {
      console.log('Block height changed from', previousBlockHeight, 'to', blockHeight);
      setBlockChangeAnimation(true);
      setTimeout(() => {
        setBlockChangeAnimation(false);
      }, 1000); // Animation duration
    }
    setPreviousBlockHeight(blockHeight);
  }, [blockHeight, previousBlockHeight]);

  return (
    <div style={{
      height: "100vh",
      width: "100vw",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "Arial",
      backgroundColor: "#000000",
      color: "#f7931a",
      overflow: "hidden",
      scrollbarWidth: "none",
      msOverflowStyle: "none"
    }}>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&display=swap');
          
          * {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          *::-webkit-scrollbar {
            display: none;
          }
          @keyframes scaleGlow {
            0% {
              transform: scale(1);
              opacity: 0;
            }
             
            25% {
              transform: scale(5);
              opacity: 0.1;
            }
            50% {
              transform: scale(10);
              opacity: 0.2;
            }  
            99% {
              transform: scale(10);
              opacity: 0;
            }
              100% {
              transform: scale(1);
              opacity: 0;
            }
          }
          @keyframes tickTockPulse {
            0% {
              transform: scale(0.97);
              opacity: 0.95;
              textShadow: 1px 1px 2px rgba(0,0,0,0.5), 0 0 6px rgba(255,255,255,0.4);
            }
            10% {
              transform: scale(0.97);
              opacity: 0.95;
              textShadow: 1px 1px 2px rgba(0,0,0,0.5), 0 0 6px rgba(255,255,255,0.4);
            }
            20% {
              transform: scale(1.08);
              opacity: 1;
              textShadow: 1px 1px 2px rgba(0,0,0,0.5), 0 0 15px rgba(255,255,255,0.8), 0 0 25px rgba(255,255,255,0.3);
            }
            30% {
              transform: scale(1.08);
              opacity: 1;
              textShadow: 1px 1px 2px rgba(0,0,0,0.5), 0 0 15px rgba(255,255,255,0.8), 0 0 25px rgba(255,255,255,0.3);
            }
            40% {
              transform: scale(0.97);
              opacity: 0.95;
              textShadow: 1px 1px 2px rgba(0,0,0,0.5), 0 0 6px rgba(255,255,255,0.4);
            }
            50% {
              transform: scale(0.97);
              opacity: 0.95;
              textShadow: 1px 1px 2px rgba(0,0,0,0.5), 0 0 6px rgba(255,255,255,0.4);
            }
            60% {
              transform: scale(1.08);
              opacity: 1;
              textShadow: 1px 1px 2px rgba(0,0,0,0.5), 0 0 15px rgba(255,255,255,0.8), 0 0 25px rgba(255,255,255,0.3);
            }
            70% {
              transform: scale(1.08);
              opacity: 1;
              textShadow: 1px 1px 2px rgba(0,0,0,0.5), 0 0 15px rgba(255,255,255,0.8), 0 0 25px rgba(255,255,255,0.3);
            }
            80% {
              transform: scale(0.97);
              opacity: 0.95;
              textShadow: 1px 1px 2px rgba(0,0,0,0.5), 0 0 6px rgba(255,255,255,0.4);
            }
            90% {
              transform: scale(0.97);
              opacity: 0.95;
              textShadow: 1px 1px 2px rgba(0,0,0,0.5), 0 0 6px rgba(255,255,255,0.4);
            }
            100% {
              transform: scale(0.97);
              opacity: 0.95;
              textShadow: 1px 1px 2px rgba(0,0,0,0.5), 0 0 6px rgba(255,255,255,0.4);
            }
          }
          @keyframes wavyText {
            0%, 100% { transform: translateY(0px); }
            25% { transform: translateY(-3px); }
            50% { transform: translateY(0px); }
            75% { transform: translateY(3px); }
          }
          @keyframes textFade {
            0% {
              opacity: 1;
              transform: translateY(0px);
            }
            50% {
              opacity: 0;
              transform: translateY(-5px);
            }
            100% {
              opacity: 1;
              transform: translateY(0px);
            }
          }
          @keyframes blockNumberChange {
            0% {
              opacity: 0;
              transform: scale(0.8);
              color: #f7931a;
              textShadow: 0 0 20px rgba(247, 147, 26, 0.5);
            }
            50% {
              opacity: 1;
              transform: scale(1.1);
              color: #ffffff;
              textShadow: 0 0 40px rgba(255, 255, 255, 0.9);
            }
            100% {
              opacity: 1;
              transform: scale(1);
              color: #f7931a;
              textShadow: 0 0 20px rgba(247, 147, 26, 0.5);
            }
          }
        `}
      </style>
      {/* Screen Indicator */}
      <div style={{
        fontSize: "1.5vw",
        color: "#666",
        
      }}>
        BITCOIN CLOCK - HALVING BLOCK TIME
      </div>

      {/* Current Block Height */}
      <div style={{
        fontSize: "10vw",
        fontWeight: "900",
        color: "#f7931a",
        fontFamily: "'Orbitron', 'Courier New', monospace",
        textShadow: "0 0 20px rgba(247, 147, 26, 0.5)",
        marginBottom: "1vh",
        animation: blockChangeAnimation ? "blockNumberChange 1s ease-in-out" : "none",
        letterSpacing: "0.1em",
        textAlign: "center"
      }}>
        {blockHeight === null ? "LOADING..." : blockHeight.toLocaleString()}
      </div>

     

      {/* Timeline Visualization */}
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        maxWidth: "60vw",
        marginBottom: "3vh"
      }}>
        {/* Circles Row */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          maxWidth: "60vw",
          position: "relative",
          marginBottom: "2vh"
        }}>
          {timelineItems.map((item, index) => (
            <div key={`circle-${index}`} style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: "1",
              minWidth: "80px"
            }}>
              <div style={{
                width: "max(10vw, 100px)",
                height: "max(10vw, 100px)",
                borderRadius: "50%",
                backgroundColor: getItemColor(item),
                border: "0.2vw solid #333",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 2
              }}>
                {/* Circle Text */}
                <span style={{
                  color: "#ffffff",
                  fontSize: item.type === 'current' ? "max(4vw, 40px)" : 
                           (item.type === 'prev-halving' || item.type === 'next-halving') ? "max(1.8vw, 18px)" : "max(2.4vw, 24px)",
                  fontWeight: "bold",
                  fontFamily: "Arial, sans-serif",
                  textShadow: "1px 1px 2px rgba(0,0,0,0.7)",
                  textAlign: "center",
                  lineHeight: "1.2",
                  zIndex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  transform: item.type === 'current' ? "rotate(14deg)" : "none"
                }}>
                  {item.type === 'current' ? 
                     <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                       <div style={{ fontSize: "max(6.5vw, 65px)", transform: "rotate(14deg)" }}>₿</div>
                     </div> : 
                   item.type === 'prev-halving' ? 
                     <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                       <div style={{ fontSize: "max(1.5vw, 15px)", marginBottom: "0.2vh" }}>PREV</div>
                       <div style={{ fontSize: "max(1.5vw, 15px)", marginBottom: "0.2vh" }}>HALVING</div>
                       <div>{`${getPrevHalvingElapsedPercent()}%`}</div>
                       <div style={{ fontSize: "max(1vw, 10px)", marginTop: "0.2vh" }}>
                         {halvingData && blockHeight ? `${getDaysSinceHalving(halvingData.halvings[currentHalvingIndex].timestamp)}d ago` : ''}
                       </div>
                     </div> : 
                   item.type === 'next-halving' ? 
                     <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                       <div style={{ fontSize: "max(1.5vw, 15px)", marginBottom: "0.2vh" }}>NEXT</div>
                       <div style={{ fontSize: "max(1.5vw, 15px)", marginBottom: "0.2vh" }}>HALVING</div>
                       <div>{`${getHalvingProgressPercent()}%`}</div>
                       <div style={{ fontSize: "max(1vw, 10px)", marginTop: "0.2vh" }}>
                         {halvingData && blockHeight && halvingData.halvings[currentHalvingIndex + 1] ? 
                           `${getDaysUntilHalving(halvingData.halvings[currentHalvingIndex + 1].timestamp)}d left` : ''}
                       </div>
                     </div> : ''}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Status */}
        <div style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginBottom: "2vh",
          width: "100%",
          maxWidth: "60vw"
        }}>
          {/* Parent box containing all status boxes */}
          <div style={{
            display: "flex",
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            borderRadius: "8px",
            padding: "1vh 2vw",
            width: "100%",
            gap: "0.5vw"
          }}>
            {/* Previous Halving */}
            <div style={{
              flex: "1",
              backgroundColor: "rgba(0, 255, 0, 0.1)",
              border: "1px solid rgba(0, 255, 0, 0.5)",
              borderRadius: "4px",
              padding: "0.5vh 1vw",
              fontSize: "1.2vw",
              color: "#00ff00",
              fontWeight: "bold",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "0"
            }}>
              <div>Block Progress</div>
              <div style={{ fontSize: "1.5vw", marginTop: "0.2vh" }}>
                {halvingData && blockHeight && halvingData.halvings[currentHalvingIndex] ? 
                  `${(blockHeight - halvingData.halvings[currentHalvingIndex].block).toLocaleString()} blocks` : ''}
              </div>
            </div>

            {/* Arrow */}
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 1vw",
              fontSize: "2vw",
              color: "#f7931a",
              fontWeight: "bold"
            }}>
              →
            </div>

            {/* Current Block */}
            <div style={{
              flex: "1",
              backgroundColor: "rgba(247, 147, 26, 0.1)",
              border: "1px solid rgba(247, 147, 26, 0.5)",
              borderRadius: "4px",
              padding: "0.5vh 1vw",
              fontSize: "1.2vw",
              color: "#f7931a",
              fontWeight: "bold",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "0"
            }}>
              <div>BITCOIN</div>
              <div style={{ fontSize: "1.5vw", marginTop: "0.2vh" }}>
                ${btcPrice ? btcPrice.toLocaleString() : "LOADING..."}
              </div>
            </div>

            {/* Arrow */}
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 1vw",
              fontSize: "2vw",
              color: "#f7931a",
              fontWeight: "bold"
            }}>
              →
            </div>


            {/* Future Halving */}
            <div style={{
              flex: "1",
              backgroundColor: "rgba(0, 149, 255, 0.1)",
              border: "1px solid rgba(0, 149, 255, 0.5)",
              borderRadius: "4px",
              padding: "0.5vh 1vw",
              fontSize: "1.2vw",
              color: "#0095ff",
              fontWeight: "bold",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "0"
            }}>
              <div>Blocks Remaining</div>
              <div style={{ fontSize: "1.5vw", marginTop: "0.2vh" }}>
                {halvingData && blockHeight && halvingData.halvings[currentHalvingIndex + 1] ? 
                  `${(halvingData.halvings[currentHalvingIndex + 1].block - blockHeight).toLocaleString()}` : ''}
              </div>
            </div>
          </div>
        </div>

        {/* Data Row */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          width: "100%",
          maxWidth: "60vw"
        }}>
          {timelineItems.map((item, index) => (
            <div key={`data-${index}`} style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              flex: "1",
              minWidth: "80px"
            }}>
              
             
              {/* Reward for halving items */}
              {(item.type === 'prev-halving' || item.type === 'next-halving') && (
                <div style={{
                  fontSize: "1.8vw",
                  color: getItemColor(item),
                  fontFamily: "Courier New, monospace",
                  marginBottom: "1.2vh"
                }}>
                  {item.reward} BTC
                </div>
              )}

              {/* Months Information */}
              {(item.type === 'prev-halving' || item.type === 'next-halving') && (
                <div style={{
                  fontSize: "1.4vw",
                  color: "#ffffff",
                  fontFamily: "Arial, sans-serif",
                  fontWeight: "bold",
                  marginBottom: "1vh",
                  textAlign: "center",
                  textShadow: "1px 1px 2px rgba(0,0,0,0.5)"
                }}>
                  {item.type === 'prev-halving'
                    ? `${getMonthsSinceHalving(item.time)} months ago`
                    : `${getMonthsUntilHalving(item.time)} months left`
                  }
                </div>
              )}

              {/* Price for current block */}
              {item.type === 'current' && btcPrice && (
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  marginBottom: "0.3vh" // Reduced from 1vh
                }}>
                  {/*  MOTIVATIONAL TEXT - First Part */}
                  <div style={{
                    fontSize: "1.7vw",
                    color: "#ffffffff",
                    fontFamily: "Arial, sans-serif",
                    marginBottom: "0.1vh", // Reduced from 0.4vh
                    textShadow: "1px 1px 2px rgba(0,0,0,0.5)",
                    animation: textTransition ? "textFade 0.6s ease-in-out" : "none",
                    transition: "all 0.3s ease-in-out"
                  }}>
                    {motivationalTexts[currentTextIndex].first}
                  </div>
                  {/*  MOTIVATIONAL TEXT - Second Part */}
                  <div style={{
                    fontSize: "1.8vw",
                    marginTop: "1.2vh", // Reduced from 0.3vh
                    marginBottom: "0.1vh", // Reduced from 0.4vh
                    color: getItemColor(item),
                    fontFamily: "Courier New, monospace",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    animation: textTransition ? "textFade 0.6s ease-in-out 0.1s both" : "none",
                    transition: "all 0.3s ease-in-out"
                  }}>
                    {motivationalTexts[currentTextIndex].second.split("").map((char, index) => (
                      <span
                        key={index}
                        style={{
                          display: "inline-block",
                          margin: "0 0.05em"
                        }}
                      >
                        {char === " " ? "\u00A0" : char}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Current Date - moved outside motivational text section */}
              {item.type === 'current' && btcPrice && (
                <div style={{
                  fontSize: "1.6vw",
                  color: "#666",
                  textAlign: "center",
                  marginTop: "0.2vh" // Reduced from 0.6vh
                }}>
                  {new Date().toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </div>
              )}

              {/* Date Label - Only show for halving items */}
              {(item.type === 'prev-halving' || item.type === 'next-halving') && (
                <div style={{
                  fontSize: "1.6vw",
                  color: "#666",
                  textAlign: "center",
                  marginTop: "0.2vh" // Reduced from 0.6vh
                }}>
                  {formatDate(item.time)}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BitcoinClockWatchTimeline;



// WEBPACK FOOTER //
// ./src/screens/BitcoinClockWatchTimeline.js